import type { Card } from '@mtg-deck-tool/shared';
import { getCardManaCost, getCardTypeLine, isDoubleFaced } from '@mtg-deck-tool/shared';

import cardtraderSets from '../../data/cardtrader-sets.json';

const ctSets = cardtraderSets as Record<string, string>;

function cardTraderUrl(card: Card): string {
  const slugify = (s: string) =>
    s.toLowerCase().replace(/'/g, '').replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-');
  const setSlug = ctSets[card.set.toLowerCase()];
  if (!setSlug) return `https://www.cardtrader.com/en/manasearch_results?q=${encodeURIComponent(card.name)}`;
  return `https://www.cardtrader.com/en/cards/${slugify(card.name)}-${setSlug}`;
}
import { useDeckStore } from '../../stores/deck-store.js';
import { useCardPreview } from '../../contexts/CardPreviewContext.js';
import { ManaCostIcons } from '../shared/ManaCostIcons.js';
import { useSymbolMap } from '../../hooks/useSymbolMap.js';

interface SearchResultRowProps {
  card: Card;
}

export function SearchResultRow({ card }: SearchResultRowProps) {
  const { setHoveredCard } = useCardPreview();
  const symbolMap = useSymbolMap();

  const addCard = useDeckStore((s) => s.addCard);
  const removeCard = useDeckStore((s) => s.removeCard);
  const addToSideboard = useDeckStore((s) => s.addToSideboard);
  const removeFromSideboard = useDeckStore((s) => s.removeFromSideboard);
  const entries = useDeckStore((s) => s.entries);
  const sideboard = useDeckStore((s) => s.sideboard);
  const flippedCards = useDeckStore((s) => s.flippedCards);
  const toggleFlip = useDeckStore((s) => s.toggleFlip);
  const openCardDetail = useDeckStore((s) => s.openCardDetail);

  const deckEntry = entries.find((e) => e.card.oracleId === card.oracleId);
  const inDeck = deckEntry !== undefined;
  const inSideboard = sideboard.some((c) => c.card.oracleId === card.oracleId);

  const isDfc = isDoubleFaced(card);
  const isFlipped = flippedCards.has(card.oracleId);

  const displayCard: Card = isDfc && isFlipped && card.cardFaces && card.cardFaces.length >= 2
    ? { ...card, cardFaces: [card.cardFaces[1]!, card.cardFaces[0]!] }
    : card;

  const manaCost = getCardManaCost(card);
  const fullType = getCardTypeLine(card);
  // Show just main type (before —), e.g. "Legendary Creature" or "Artifact"
  const shortType = fullType.includes('—') ? fullType.split('—')[0]!.trim() : fullType;
  const price = card.prices.eur;

  return (
    <div
      className="flex items-center gap-1.5 px-3 py-1.5 border-b border-gray-800/50 hover:bg-gray-800/40 transition-colors group"
      onMouseEnter={() => setHoveredCard(displayCard)}
      onMouseLeave={() => setHoveredCard(null)}
    >
      {/* Quantity / status (left) */}
      {inDeck ? (
        <span className="text-[10px] text-orange-400 font-bold tabular-nums shrink-0 w-4 text-right">
          {deckEntry.quantity}
        </span>
      ) : inSideboard ? (
        <span className="text-[10px] text-blue-400 font-bold shrink-0 w-4 text-right">?</span>
      ) : (
        <span className="w-4 shrink-0" />
      )}

      {/* Card name + type */}
      <span className="text-xs font-medium text-gray-200 truncate min-w-0 flex-1 inline-flex items-center gap-1">
        <button
          onClick={() => { setHoveredCard(card); openCardDetail(card, false); }}
          className="text-xs text-gray-200 truncate hover:text-orange-300 hover:underline cursor-pointer bg-transparent border-none p-0 text-left"
        >
          {card.name}
        </button>
        {isDfc && (
          <button
            onClick={(e) => { e.stopPropagation(); toggleFlip(card.oracleId); }}
            className="text-[10px] text-gray-500 hover:text-gray-300 px-0.5 shrink-0 transition-colors"
            title="Flip card"
          >
            &#x21BB;
          </button>
        )}
        {card.gameChanger && <span className="text-[9px] font-bold text-red-400 bg-red-900/40 border border-red-700/50 rounded px-1 py-px shrink-0 leading-none">GC</span>}
        {shortType && <span className="text-[10px] text-gray-600 font-normal">{shortType}</span>}
      </span>

      {/* Price (right) — links to CardTrader */}
      {price && (
        <a
          href={cardTraderUrl(card)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-[10px] text-green-400 hover:text-green-300 font-medium shrink-0 tabular-nums hover:underline"
          title="Search on CardTrader"
        >
          €{price}
        </a>
      )}

      {/* Mana cost (right) */}
      {manaCost && (
        <ManaCostIcons manaCost={manaCost} symbolMap={symbolMap} size={12} className="shrink-0" />
      )}

      {/* Action buttons (right, hover only) */}
      <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        {inDeck ? (
          <button
            onClick={() => removeCard(deckEntry.card.id)}
            className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-700/50 hover:bg-red-900/50 text-gray-400 hover:text-red-300 rounded transition-colors"
          >
            ✕
          </button>
        ) : inSideboard ? (
          <>
            <button
              onClick={() => { removeFromSideboard(card.oracleId); addCard(card); }}
              className="px-1.5 py-0.5 text-[10px] font-medium bg-orange-600/20 hover:bg-orange-600 text-orange-400 hover:text-white rounded transition-colors border border-orange-700/40 hover:border-orange-600"
              title="Move to Deck"
            >
              +
            </button>
            <button
              onClick={() => removeFromSideboard(card.oracleId)}
              className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-700/50 hover:bg-red-900/50 text-gray-400 hover:text-red-300 rounded transition-colors"
              title="Remove from Sideboard"
            >
              ✕
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => addCard(card, true)}
              className="px-1.5 py-0.5 text-[10px] font-medium bg-orange-600/20 hover:bg-orange-600 text-orange-400 hover:text-white rounded transition-colors border border-orange-700/40 hover:border-orange-600"
              title="Add as Commander"
            >
              ⚔
            </button>
            <button
              onClick={() => addCard(card)}
              className="px-1.5 py-0.5 text-[10px] font-medium bg-orange-600/20 hover:bg-orange-600 text-orange-400 hover:text-white rounded transition-colors border border-orange-700/40 hover:border-orange-600"
              title="Add to Deck"
            >
              +
            </button>
            <button
              onClick={() => addToSideboard(card)}
              className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white rounded transition-colors border border-blue-700/40 hover:border-blue-600"
              title="Add to Sideboard"
            >
              ?
            </button>
          </>
        )}
      </div>
    </div>
  );
}
